import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ErpConnectionView, ErpIssue, ErpIssueStatus, ErpSyncRun } from '../../api';
import { erpApi } from '../../api';
import { useResource } from '../../hooks';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { ErrorNotice, LoadingRows } from '../ui';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { Timestamp } from '../status/StatusPieces';
import {
  failureClassLabel,
  formatNumber,
  issueCodeLabel,
  issueFixedBy,
  issueSampleTab,
  issueSeverityLabel,
  issueSeverityLevel,
  labelOr,
  linkedWebsites,
  runKindLabel,
  runStateLabel,
  runTriggerLabel,
  severalStores,
  websiteKey,
  websiteLabel,
} from './labels';
import { useAction } from './useAction';
import { actionLabel } from '../operation/actionLabels';
import { Select } from '../primitives';

/**
 * Issues: everything that needs a person, most serious first. Each says
 * what it affects, what happens, who fixes it and how often it was seen
 * (CO-158). A held price can be accepted here; a configuration gap is fixed
 * in the store and closes itself on the next sync.
 */
export function ErpIssuesPanel({
  connection,
  canManage,
  onChanged,
  limit,
  onShowAll,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  onChanged: () => void;
  /** Show only the first few open issues (the overview's short list). */
  limit?: number;
  /** Opens the Issues view from the short list. */
  onShowAll?: () => void;
}) {
  const [status, setStatus] = useState<ErpIssueStatus>('open');
  const [reload, setReload] = useState(0);
  const action = useAction();
  const issues = useResource(
    () => erpApi.issues(connection.id, status),
    [connection.id, status, reload, connection.last_stock_sync_at],
    connection.id, { key: `erp:${connection.id}:issues:${status}` },
  );
  const list = issues.data?.issues ?? [];
  const shown = limit ? list.slice(0, limit) : list;

  const act = (issue: ErpIssue, verb: 'accept' | 'ignore' | 'resolve', done: string) =>
    action.run(
      `${verb}:${issue.id}`,
      async () => {
        await erpApi.actOnIssue(issue.id, verb);
        setReload((value) => value + 1);
        onChanged();
      },
      done,
    );

  const titleId = limit ? 'erp-issues-short-title' : 'erp-issues-title';
  return (
    <PanelCard labelledBy={titleId} testId={limit ? 'erp-issues-short' : 'erp-issues'}>
      <SectionHeader
        id={titleId}
        title={limit ? 'Open issues' : 'Issues'}
        help={
          limit ? undefined : (
            <p>An issue closes itself on the next sync once its cause is gone. Ignored issues are not raised again.</p>
          )
        }
        helpLabel="How issues work"
        actions={
          limit ? (
            list.length > limit && onShowAll ? (
              <button type="button" className="button button--secondary button--small" onClick={onShowAll}>
                View all {list.length} issues
              </button>
            ) : null
          ) : (
            <div className="field erp-inline-select">
              <label htmlFor="erp-issue-status">Show</label>
              <Select id="erp-issue-status" value={status} onChange={(event) => setStatus(event.target.value as ErpIssueStatus)}>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
                <option value="ignored">Ignored</option>
              </Select>
            </div>
          )
        }
      />
      <ErrorNotice error={issues.error ?? action.error} />
      {action.notice ? (
        <p className="panel__note" role="status">
          {action.notice}
        </p>
      ) : null}
      {issues.loading && !issues.data ? (
        <LoadingRows label="Loading issues" rows={3} />
      ) : shown.length === 0 ? (
        <QuietEmpty testId="erp-no-issues">{status === 'open' ? 'No open issues' : `No ${status} issues`}</QuietEmpty>
      ) : (
        <ul className="erp-issue-list">
          {shown.map((issue) => (
            <IssueItem
              key={issue.id}
              issue={issue}
              connection={connection}
              actions={
                canManage && issue.status === 'open' ? (
                  <>
                    {issue.code === 'price_change_held' ? (
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        disabled={action.busy !== null}
                        onClick={() => void act(issue, 'accept', 'The new price was sent to the store.')}
                      >
                        {action.busy === `accept:${issue.id}` ? 'Sending' : 'Accept and send the new price'}
                      </button>
                    ) : null}
                    {!(issue.severity === 'blocking' && issue.category === 'configuration') ? (
                      <button
                        type="button"
                        className="button button--ghost button--small"
                        disabled={action.busy !== null}
                        onClick={() => void act(issue, 'ignore', 'Ignored. It will not be raised again.')}
                      >
                        Ignore
                      </button>
                    ) : null}
                  </>
                ) : null
              }
            />
          ))}
        </ul>
      )}
    </PanelCard>
  );
}

function IssueItem({ issue, connection, actions }: { issue: ErpIssue; connection: ErpConnectionView; actions: React.ReactNode }) {
  const fixedBy = issueFixedBy[issue.code];
  return (
    <li className={`erp-issue erp-issue--${issue.severity}`} data-testid="erp-issue">
      <div className="erp-issue__head">
        <p className="erp-issue__title">{labelOr(issueCodeLabel, issue.code)}</p>
        <LevelBadge level={issueSeverityLevel[issue.severity]}>{issueSeverityLabel[issue.severity]}</LevelBadge>
      </div>
      <dl className="erp-issue__facts">
        <div>
          <dt>Affects</dt>
          <dd>
            {affectedLabel(issue, connection)}
            <IssueSample issue={issue} />
          </dd>
        </div>
        <div>
          <dt>What happens</dt>
          <dd>{issue.message}</dd>
        </div>
        {fixedBy ? (
          <div>
            <dt>Required action</dt>
            <dd data-testid="erp-issue-owner">{fixedBy}</dd>
          </div>
        ) : null}
        <div>
          <dt>Seen</dt>
          <dd>
            {issue.occurrences > 1 ? `${formatNumber(issue.occurrences)} times, last ` : ''}
            <Timestamp iso={issue.occurrences > 1 ? issue.last_seen_at : issue.first_seen_at} />
            {issue.occurrences > 1 ? (
              <>
                {', first '}
                <Timestamp iso={issue.first_seen_at} />
              </>
            ) : null}
            {issue.resolved_by ? `. Closed by ${issue.resolved_by}` : ''}
          </dd>
        </div>
      </dl>
      {actions ? <div className="erp-actions">{actions}</div> : null}
    </li>
  );
}

/** The website, store or connection an issue is about. */
function affectedLabel(issue: ErpIssue, connection: ErpConnectionView): string {
  if (!issue.installation_id) return 'Every website on this connection';
  const linked = linkedWebsites(connection);
  const entry = linked.find((item) => item.store.id === issue.installation_id && item.website.code === (issue.website_code ?? ''));
  if (entry) return websiteLabel(entry.store, entry.website, severalStores(linked));
  const store = connection.stores.find((item) => item.id === issue.installation_id);
  if (!store) return 'A store no longer on this connection';
  return issue.website_code ? `${issue.website_code} (${store.name})` : store.name;
}

function IssueSample({ issue }: { issue: ErpIssue }) {
  const [searchParams] = useSearchParams();
  const sample = issue.details?.sample;
  // The products an issue names, each opening where it can be looked at
  // (UX-19): an aggregate issue lists a sample, a single one its subject.
  const values = Array.isArray(sample) && sample.length > 0 ? sample.map(String) : issue.subject_key && issueSampleTab[issue.code] ? [issue.subject_key] : [];
  if (values.length === 0) return null;
  const count = typeof issue.details?.count === 'number' ? issue.details.count : values.length;
  const tab = issueSampleTab[issue.code];
  const hrefOf = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab!);
    params.set('q', value);
    // The store and website the issue is about (CO-134).
    if (issue.installation_id) params.set('store', issue.website_code ? websiteKey(issue.installation_id, issue.website_code) : issue.installation_id);
    else params.delete('store');
    return `?${params.toString()}`;
  };
  return (
    <span className="erp-issue__sample">
      {count === 1 ? 'Product: ' : 'Products: '}
      {values.map((value, index) => (
        <span key={value}>
          {index > 0 ? ', ' : ''}
          {tab ? (
            <Link to={hrefOf(value)} aria-label={`Look up ${value} in ${tab === 'matching' ? 'Product matching' : 'Inventory'}`}>
              {value}
            </Link>
          ) : (
            value
          )}
        </span>
      ))}
      {count > values.length ? ` and ${formatNumber(count - values.length)} more` : ''}
    </span>
  );
}

function runLevel(run: ErpSyncRun) {
  return run.state === 'succeeded' ? 'ok' : run.state === 'failed' ? 'critical' : 'unknown';
}

function duration(run: ErpSyncRun): string {
  if (!run.stats.duration_ms) return '—';
  const seconds = run.stats.duration_ms / 1000;
  return seconds < 60 ? `${seconds.toFixed(1)} s` : `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`;
}

function updatesSent(run: ErpSyncRun): number {
  return run.stats.quantities_sent + run.stats.prices_sent + (run.stats.products_sent ?? 0) + (run.stats.imports_requested ?? 0);
}

/**
 * Sync history: every sync with the ERP, with what it read, what changed
 * and what was sent (CO-158). A failed sync changed nothing in any store;
 * its details say why.
 */
export function ErpRunsPanel({
  connection,
  limit,
  onShowAll,
  openRunId,
}: {
  connection: ErpConnectionView;
  limit?: number;
  onShowAll?: () => void;
  /** A run whose details start open: where "View error" from the sync problem lands. */
  openRunId?: string;
}) {
  const runs = useResource(
    () => erpApi.runs(connection.id, limit ?? 50),
    [connection.id, limit, connection.last_stock_sync_at, connection.last_tested_at],
    connection.id, { key: `erp:${connection.id}:runs:${limit ?? 50}` },
  );
  const [open, setOpen] = useState<string | null>(openRunId ?? null);
  const list = runs.data?.runs ?? [];
  // Bring the run named by the address into view once the list has loaded.
  const target = openRunId && list.some((run) => run.id === openRunId) ? openRunId : null;
  useEffect(() => {
    if (!target) return;
    document.querySelector(`[data-testid="erp-run-${target}"]`)?.scrollIntoView?.({ block: 'center' });
  }, [target]);
  const short = Boolean(limit);

  const columns: Column<ErpSyncRun>[] = [
    {
      id: 'started',
      header: 'Started',
      card: 'title',
      width: 'time',
      cell: (run) => (
        <span className="cell-stack">
          <Timestamp iso={run.started_at} />
          <span className="cell-stack__secondary">
            {labelOr(runKindLabel, run.kind)}, {labelOr(runTriggerLabel, run.trigger).toLowerCase()}
          </span>
        </span>
      ),
    },
    {
      id: 'result',
      header: 'Result',
      card: 'status',
      width: 'status',
      cell: (run) => (
        <span className="cell-stack">
          <LevelBadge level={runLevel(run)}>{labelOr(runStateLabel, run.state)}</LevelBadge>
          {/* The cause on the row itself (IN3); the whole message is under View error. */}
          {run.state === 'failed' ? (
            <span className="erp-run-cause" data-testid={`erp-run-cause-${run.id}`}>
              {run.error_message ? `${run.error_class ? `${labelOr(failureClassLabel, run.error_class)}: ` : ''}${run.error_message}` : 'No reason recorded'}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'read',
      header: <span className="rt-num">Positions read</span>,
      cardLabel: 'Positions read',
      width: 14,
      cell: (run) => (
        <span className="rt-num tabular-nums">
          {run.state === 'succeeded' ? formatNumber(run.kind === 'stock' ? run.stats.positions : run.stats.items_reported) : '—'}
        </span>
      ),
    },
    {
      id: 'changes',
      header: <span className="rt-num">Changes detected</span>,
      cardLabel: 'Changes detected',
      width: 14,
      cell: (run) => (
        <span className="rt-num tabular-nums">
          {run.kind === 'stock' && run.state === 'succeeded' ? formatNumber(run.stats.items_changed + run.stats.prices_changed) : '—'}
        </span>
      ),
    },
    {
      id: 'sent',
      header: <span className="rt-num">Updates sent</span>,
      cardLabel: 'Updates sent',
      width: 14,
      cell: (run) => (
        <span className="rt-num tabular-nums">
          {run.kind === 'stock' && run.state === 'succeeded' ? formatNumber(updatesSent(run)) : '—'}
          {run.stats.prices_held > 0 ? <span className="cell-stack__secondary">{run.stats.prices_held} held</span> : null}
        </span>
      ),
    },
    {
      id: 'duration',
      header: <span className="rt-num">Duration</span>,
      cardLabel: 'Duration',
      width: 10,
      priority: 'secondary',
      cell: (run) => <span className="rt-num tabular-nums">{duration(run)}</span>,
    },
  ];

  const hasDetails = (run: ErpSyncRun) => Boolean(run.error_message || run.stats.credential_rotated || run.correlation_id);

  return (
    <PanelCard labelledBy={short ? 'erp-runs-short-title' : 'erp-runs-title'} testId={short ? 'erp-runs-short' : 'erp-runs'}>
      <SectionHeader
        id={short ? 'erp-runs-short-title' : 'erp-runs-title'}
        title={short ? 'Recent syncs' : 'Sync history'}
        description={short ? undefined : 'Kept for 30 days.'}
        actions={
          short && onShowAll && list.length > 0 ? (
            <button type="button" className="button button--secondary button--small" onClick={onShowAll}>
              View sync history
            </button>
          ) : null
        }
      />
      <ErrorNotice error={runs.error} />
      {runs.loading && !runs.data ? (
        <LoadingRows label="Loading history" rows={4} />
      ) : list.length === 0 ? (
        <QuietEmpty>No sync yet</QuietEmpty>
      ) : (
        <ResponsiveTable
          rows={list}
          rowKey={(run) => run.id}
          caption={short ? 'Recent syncs' : 'Sync history'}
          className="erp-runs-table"
          columns={columns}
          actionsSize="md"
          actions={(run) =>
            hasDetails(run) ? (
              <button
                type="button"
                className="button button--ghost button--small"
                aria-expanded={open === run.id}
                aria-label={`${open === run.id ? 'Hide details' : run.state === 'failed' ? 'View error' : 'View details'} of the sync started ${new Date(run.started_at).toLocaleString()}`}
                onClick={() => setOpen((current) => (current === run.id ? null : run.id))}
              >
                {open === run.id ? actionLabel.hideDetails : run.state === 'failed' ? actionLabel.viewError : actionLabel.viewDetails}
              </button>
            ) : null
          }
          detail={(run) => (open === run.id ? <RunDetails run={run} /> : null)}
          rowProps={(run) => ({ testId: `erp-run-${run.id}` })}
        />
      )}
    </PanelCard>
  );
}

function RunDetails({ run }: { run: ErpSyncRun }) {
  return (
    <dl className="erp-run-details" data-testid="erp-run-details">
      {run.error_message ? (
        <div>
          <dt>Error</dt>
          <dd>
            {labelOr(failureClassLabel, run.error_class)}: {run.error_message}
          </dd>
        </div>
      ) : null}
      {run.stats.credential_rotated ? (
        <div>
          <dt>Access key</dt>
          <dd>The ERP issued a new key; it was stored.</dd>
        </div>
      ) : null}
      {run.kind === 'stock' && run.state === 'succeeded' ? (
        <div>
          <dt>Sent</dt>
          <dd>
            {formatNumber(run.stats.quantities_sent)} quantities, {formatNumber(run.stats.prices_sent)} prices
            {run.stats.products_sent ? `, ${formatNumber(run.stats.products_sent)} product updates` : ''}
            {run.stats.imports_requested ? `, ${formatNumber(run.stats.imports_requested)} new products` : ''} to{' '}
            {run.stats.stores_updated === 1 ? '1 store' : `${formatNumber(run.stats.stores_updated)} stores`}
          </dd>
        </div>
      ) : null}
      {run.correlation_id ? (
        <div>
          <dt>Correlation ID</dt>
          <dd>
            <code>{run.correlation_id}</code>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
